import type {
  BoardFloorPlanData,
  FloorPlanConstraint,
  FloorPlanOpening,
  FloorPlanWall,
  FloorPlanWallEnd,
} from '../../../types';
import {
  GRID_MINOR_IN,
  MIN_WALL_LENGTH_IN,
  clampOpeningOnWall,
  genFloorPlanId,
  normalizePlanExtents,
  snapInches,
  wallLength,
} from './floorPlanMath';

export const JOIN_THRESHOLD_IN = 8;

export function wallAxis(wall: Pick<FloorPlanWall, 'x1' | 'y1' | 'x2' | 'y2'>): 'h' | 'v' {
  return Math.abs(wall.x2 - wall.x1) >= Math.abs(wall.y2 - wall.y1) ? 'h' : 'v';
}

export function wallEndPoint(
  wall: Pick<FloorPlanWall, 'x1' | 'y1' | 'x2' | 'y2'>,
  end: FloorPlanWallEnd,
): { x: number; y: number } {
  return end === 'start' ? { x: wall.x1, y: wall.y1 } : { x: wall.x2, y: wall.y2 };
}

export function otherWallEnd(end: FloorPlanWallEnd): FloorPlanWallEnd {
  return end === 'start' ? 'end' : 'start';
}

export function setWallEnd(wall: FloorPlanWall, end: FloorPlanWallEnd, x: number, y: number): FloorPlanWall {
  return end === 'start' ? { ...wall, x1: x, y1: y } : { ...wall, x2: x, y2: y };
}

function endpointKey(wallId: string, end: FloorPlanWallEnd): string {
  return `${end}::${wallId}`;
}

function parseEndpointKey(key: string): { wallId: string; end: FloorPlanWallEnd } {
  const end: FloorPlanWallEnd = key.startsWith('start::') ? 'start' : 'end';
  return { end, wallId: key.slice(end.length + 2) };
}

export function sameConstraintPair(
  aWallId: string,
  bWallId: string,
  aEnd: FloorPlanWallEnd | undefined,
  bEnd: FloorPlanWallEnd | undefined,
  constraint: FloorPlanConstraint,
): boolean {
  const direct =
    constraint.aWallId === aWallId &&
    constraint.bWallId === bWallId &&
    (aEnd === undefined || (constraint.aEnd === aEnd && constraint.bEnd === bEnd));
  const swapped =
    constraint.aWallId === bWallId &&
    constraint.bWallId === aWallId &&
    (aEnd === undefined || (constraint.aEnd === bEnd && constraint.bEnd === aEnd));
  return direct || swapped;
}

export function hasCoincident(
  constraints: FloorPlanConstraint[],
  aWallId: string,
  aEnd: FloorPlanWallEnd,
  bWallId: string,
  bEnd: FloorPlanWallEnd,
): boolean {
  return constraints.some(
    (constraint) =>
      constraint.kind === 'coincident' &&
      sameConstraintPair(aWallId, bWallId, aEnd, bEnd, constraint),
  );
}

export function hasPerpendicular(
  constraints: FloorPlanConstraint[],
  aWallId: string,
  bWallId: string,
): boolean {
  return constraints.some(
    (constraint) =>
      constraint.kind === 'perpendicular' && sameConstraintPair(aWallId, bWallId, undefined, undefined, constraint),
  );
}

export function makeCoincident(
  aWallId: string,
  aEnd: FloorPlanWallEnd,
  bWallId: string,
  bEnd: FloorPlanWallEnd,
): FloorPlanConstraint {
  return {
    id: genFloorPlanId('co'),
    kind: 'coincident',
    aWallId,
    aEnd,
    bWallId,
    bEnd,
  };
}

export function makePerpendicular(aWallId: string, bWallId: string): FloorPlanConstraint {
  return {
    id: genFloorPlanId('perp'),
    kind: 'perpendicular',
    aWallId,
    bWallId,
  };
}

export function dropConstraintsForWall(
  constraints: FloorPlanConstraint[] | undefined,
  wallId: string,
): FloorPlanConstraint[] {
  return (constraints || []).filter(
    (constraint) => constraint.aWallId !== wallId && constraint.bWallId !== wallId,
  );
}

export function constraintsForWall(
  constraints: FloorPlanConstraint[] | undefined,
  wallId: string,
): FloorPlanConstraint[] {
  return (constraints || []).filter(
    (constraint) => constraint.aWallId === wallId || constraint.bWallId === wallId,
  );
}

function buildCoincidentGroups(constraints: FloorPlanConstraint[]): Map<string, string[]> {
  const parent = new Map<string, string>();
  const find = (key: string): string => {
    if (!parent.has(key)) parent.set(key, key);
    const current = parent.get(key)!;
    if (current !== key) {
      const root = find(current);
      parent.set(key, root);
      return root;
    }
    return key;
  };
  const union = (a: string, b: string) => {
    const pa = find(a);
    const pb = find(b);
    if (pa !== pb) parent.set(pa, pb);
  };
  for (const constraint of constraints) {
    if (constraint.kind !== 'coincident' || !constraint.aEnd || !constraint.bEnd) continue;
    union(endpointKey(constraint.aWallId, constraint.aEnd), endpointKey(constraint.bWallId, constraint.bEnd));
  }
  const groups = new Map<string, string[]>();
  parent.forEach((_value, key) => {
    const root = find(key);
    const list = groups.get(root) || [];
    list.push(key);
    groups.set(root, list);
  });
  return groups;
}

function groupForEndpoint(
  groups: Map<string, string[]>,
  wallId: string,
  end: FloorPlanWallEnd,
): string[] {
  const key = endpointKey(wallId, end);
  let match: string[] | undefined;
  groups.forEach((members) => {
    if (!match && members.includes(key)) match = members;
  });
  return match || [key];
}

function keepOrthogonal(
  wall: FloorPlanWall,
  end: FloorPlanWallEnd,
  x: number,
  y: number,
  grid = GRID_MINOR_IN,
): FloorPlanWall {
  const axis = wallAxis(wall);
  if (axis === 'h') {
    const nextY = snapInches(y, grid);
    const nextX = snapInches(x, grid);
    return end === 'start'
      ? { ...wall, x1: nextX, y1: nextY, y2: nextY }
      : { ...wall, x2: nextX, y2: nextY, y1: nextY };
  }
  const nextX = snapInches(x, grid);
  const nextY = snapInches(y, grid);
  return end === 'start'
    ? { ...wall, x1: nextX, y1: nextY, x2: nextX }
    : { ...wall, x2: nextX, y2: nextY, x1: nextX };
}

function wallById(walls: FloorPlanWall[], id: string): FloorPlanWall | undefined {
  return walls.find((wall) => wall.id === id);
}

function replaceWall(walls: FloorPlanWall[], next: FloorPlanWall): FloorPlanWall[] {
  return walls.map((wall) => (wall.id === next.id ? next : wall));
}

function shouldKeepSquare(
  constraints: FloorPlanConstraint[],
  aWallId: string,
  bWallId: string,
): boolean {
  return hasPerpendicular(constraints, aWallId, bWallId);
}

function applyVertexToGroup(
  walls: FloorPlanWall[],
  constraints: FloorPlanConstraint[],
  members: string[],
  x: number,
  y: number,
  grid = GRID_MINOR_IN,
): FloorPlanWall[] {
  let next = walls;
  for (const key of members) {
    const { wallId, end } = parseEndpointKey(key);
    const wall = wallById(next, wallId);
    if (!wall) continue;
    const others = members
      .map(parseEndpointKey)
      .filter((member) => member.wallId !== wallId)
      .map((member) => wallById(next, member.wallId))
      .filter((other): other is FloorPlanWall => !!other);
    const lockSquare = others.some((other) => shouldKeepSquare(constraints, wall.id, other.id));
    if (lockSquare || wallAxis(wall) !== wallAxis(others[0] || wall)) {
      next = replaceWall(next, keepOrthogonal(wall, end, x, y, grid));
    } else {
      next = replaceWall(next, setWallEnd(wall, end, snapInches(x, grid), snapInches(y, grid)));
    }
  }
  return next;
}

function propagate(
  walls: FloorPlanWall[],
  constraints: FloorPlanConstraint[],
  seeds: Array<{ wallId: string; end: FloorPlanWallEnd }>,
  grid = GRID_MINOR_IN,
): FloorPlanWall[] {
  const groups = buildCoincidentGroups(constraints);
  let next = walls;
  const queue = [...seeds];
  const seen = new Set<string>();
  let guard = 0;
  while (queue.length && guard < 48) {
    guard += 1;
    const seed = queue.shift()!;
    const key = endpointKey(seed.wallId, seed.end);
    if (seen.has(key)) continue;
    seen.add(key);
    const wall = wallById(next, seed.wallId);
    if (!wall) continue;
    const point = wallEndPoint(wall, seed.end);
    const members = groupForEndpoint(groups, seed.wallId, seed.end);
    const before = next;
    next = applyVertexToGroup(next, constraints, members, point.x, point.y, grid);
    for (const member of members) {
      const parsed = parseEndpointKey(member);
      if (!seen.has(member)) queue.push(parsed);
      const other = otherWallEnd(parsed.end);
      const otherKey = endpointKey(parsed.wallId, other);
      const prev = wallById(before, parsed.wallId);
      const curr = wallById(next, parsed.wallId);
      if (!prev || !curr) continue;
      const prevOther = wallEndPoint(prev, other);
      const currOther = wallEndPoint(curr, other);
      if (Math.hypot(prevOther.x - currOther.x, prevOther.y - currOther.y) > 0.01) {
        queue.push({ wallId: parsed.wallId, end: other });
        seen.delete(otherKey);
      }
    }
  }
  return next;
}

export function applyWallLength(
  walls: FloorPlanWall[],
  constraints: FloorPlanConstraint[] | undefined,
  wallId: string,
  lengthIn: number,
  anchor: FloorPlanWallEnd = 'start',
  grid = GRID_MINOR_IN,
): FloorPlanWall[] {
  const wall = wallById(walls, wallId);
  if (!wall) return walls;
  const length = Math.max(MIN_WALL_LENGTH_IN, snapInches(lengthIn, grid));
  const axis = wallAxis(wall);
  const fixed = wallEndPoint(wall, anchor);
  const moving = otherWallEnd(anchor);
  const sign =
    axis === 'h'
      ? Math.sign((moving === 'end' ? wall.x2 : wall.x1) - (anchor === 'start' ? wall.x1 : wall.x2)) || 1
      : Math.sign((moving === 'end' ? wall.y2 : wall.y1) - (anchor === 'start' ? wall.y1 : wall.y2)) || 1;
  const nextPoint =
    axis === 'h'
      ? { x: fixed.x + sign * length, y: fixed.y }
      : { x: fixed.x, y: fixed.y + sign * length };
  const updated = setWallEnd(wall, moving, nextPoint.x, nextPoint.y);
  return propagate(replaceWall(walls, updated), constraints || [], [{ wallId, end: moving }], grid);
}

export function applyWallSlide(
  walls: FloorPlanWall[],
  constraints: FloorPlanConstraint[] | undefined,
  wallId: string,
  orig: FloorPlanWall,
  dx: number,
  dy: number,
  grid = GRID_MINOR_IN,
): FloorPlanWall[] {
  const axis = wallAxis(orig);
  const slid =
    axis === 'h'
      ? { ...orig, y1: snapInches(orig.y1 + dy, grid), y2: snapInches(orig.y2 + dy, grid) }
      : { ...orig, x1: snapInches(orig.x1 + dx, grid), x2: snapInches(orig.x2 + dx, grid) };
  return propagate(replaceWall(walls, slid), constraints || [], [
    { wallId, end: 'start' },
    { wallId, end: 'end' },
  ], grid);
}

export function applyWallVertex(
  walls: FloorPlanWall[],
  constraints: FloorPlanConstraint[] | undefined,
  wallId: string,
  end: FloorPlanWallEnd,
  x: number,
  y: number,
  opts: { snap?: boolean; grid?: number } = {},
): FloorPlanWall[] {
  const wall = wallById(walls, wallId);
  if (!wall) return walls;
  const grid = opts.grid ?? GRID_MINOR_IN;
  const axis = wallAxis(wall);
  const lockedX = opts.snap || axis === 'h' ? x : wall.x1;
  const lockedY = opts.snap || axis === 'v' ? y : wall.y1;
  const updated = keepOrthogonal(wall, end, lockedX, lockedY, grid);
  if (wallLength(updated) < MIN_WALL_LENGTH_IN) return walls;
  return propagate(replaceWall(walls, updated), constraints || [], [{ wallId, end }], grid);
}

export function nearestForeignEnd(
  walls: FloorPlanWall[],
  ignoreWallId: string,
  x: number,
  y: number,
  threshold = JOIN_THRESHOLD_IN,
): { wallId: string; end: FloorPlanWallEnd } | null {
  let best: { wallId: string; end: FloorPlanWallEnd; dist: number } | null = null;
  for (const other of walls) {
    if (other.id === ignoreWallId) continue;
    for (const otherEnd of ['start', 'end'] as FloorPlanWallEnd[]) {
      const otherPoint = wallEndPoint(other, otherEnd);
      const dist = Math.hypot(x - otherPoint.x, y - otherPoint.y);
      if (dist <= threshold && (!best || dist < best.dist)) {
        best = { wallId: other.id, end: otherEnd, dist };
      }
    }
  }
  return best ? { wallId: best.wallId, end: best.end } : null;
}

export function finishWallEndDrag(
  data: BoardFloorPlanData,
  wallId: string,
  end: FloorPlanWallEnd,
  threshold = JOIN_THRESHOLD_IN,
  grid = GRID_MINOR_IN,
): BoardFloorPlanData {
  const target = nearestUnjoinedEnd(data.walls, data.constraints, wallId, end, threshold);
  if (!target) return normalizePlanExtents(data);
  const next = joinCoincident(data.walls, data.constraints, wallId, end, target, grid);
  return normalizePlanExtents(withWallEdit(data, next.walls, next.constraints));
}

export function nearestUnjoinedEnd(
  walls: FloorPlanWall[],
  constraints: FloorPlanConstraint[] | undefined,
  wallId: string,
  end: FloorPlanWallEnd,
  threshold = JOIN_THRESHOLD_IN,
): { wallId: string; end: FloorPlanWallEnd } | null {
  const wall = wallById(walls, wallId);
  if (!wall) return null;
  const point = wallEndPoint(wall, end);
  let best: { wallId: string; end: FloorPlanWallEnd; dist: number } | null = null;
  for (const other of walls) {
    if (other.id === wallId) continue;
    for (const otherEnd of ['start', 'end'] as FloorPlanWallEnd[]) {
      if (hasCoincident(constraints || [], wallId, end, other.id, otherEnd)) continue;
      const otherPoint = wallEndPoint(other, otherEnd);
      const dist = Math.hypot(point.x - otherPoint.x, point.y - otherPoint.y);
      if (dist <= threshold && (!best || dist < best.dist)) {
        best = { wallId: other.id, end: otherEnd, dist };
      }
    }
  }
  return best ? { wallId: best.wallId, end: best.end } : null;
}

export function inferConstraints(
  walls: FloorPlanWall[],
  existing: FloorPlanConstraint[] | undefined = [],
): FloorPlanConstraint[] {
  const next = [...(existing || [])];
  for (let i = 0; i < walls.length; i += 1) {
    for (const endA of ['start', 'end'] as FloorPlanWallEnd[]) {
      const pointA = wallEndPoint(walls[i], endA);
      for (let j = i + 1; j < walls.length; j += 1) {
        for (const endB of ['start', 'end'] as FloorPlanWallEnd[]) {
          const pointB = wallEndPoint(walls[j], endB);
          if (Math.hypot(pointA.x - pointB.x, pointA.y - pointB.y) > JOIN_THRESHOLD_IN) continue;
          if (!hasCoincident(next, walls[i].id, endA, walls[j].id, endB)) {
            next.push(makeCoincident(walls[i].id, endA, walls[j].id, endB));
          }
          if (
            wallAxis(walls[i]) !== wallAxis(walls[j]) &&
            !hasPerpendicular(next, walls[i].id, walls[j].id)
          ) {
            next.push(makePerpendicular(walls[i].id, walls[j].id));
          }
        }
      }
    }
  }
  return next;
}

export function joinCoincident(
  walls: FloorPlanWall[],
  constraints: FloorPlanConstraint[] | undefined,
  wallId: string,
  end: FloorPlanWallEnd,
  target: { wallId: string; end: FloorPlanWallEnd },
  grid = GRID_MINOR_IN,
): { walls: FloorPlanWall[]; constraints: FloorPlanConstraint[] } {
  const source = wallById(walls, wallId);
  const dest = wallById(walls, target.wallId);
  if (!source || !dest) return { walls, constraints: constraints || [] };
  const point = wallEndPoint(dest, target.end);
  const nextWalls = applyWallVertex(walls, constraints, wallId, end, point.x, point.y, { snap: true, grid });
  const nextConstraints = [...(constraints || [])];
  if (!hasCoincident(nextConstraints, wallId, end, target.wallId, target.end)) {
    nextConstraints.push(makeCoincident(wallId, end, target.wallId, target.end));
  }
  if (wallAxis(source) !== wallAxis(dest) && !hasPerpendicular(nextConstraints, wallId, target.wallId)) {
    nextConstraints.push(makePerpendicular(wallId, target.wallId));
  }
  return { walls: nextWalls, constraints: nextConstraints };
}

export function addPerpendicular(
  constraints: FloorPlanConstraint[] | undefined,
  aWallId: string,
  bWallId: string,
): FloorPlanConstraint[] {
  if (hasPerpendicular(constraints || [], aWallId, bWallId)) return constraints || [];
  return [...(constraints || []), makePerpendicular(aWallId, bWallId)];
}

export function clampOpeningsToWalls(
  walls: FloorPlanWall[],
  openings: FloorPlanOpening[],
): FloorPlanOpening[] {
  return openings
    .map((opening) => {
      const host = walls.find((wall) => wall.id === opening.wallId);
      if (!host) return null;
      return { ...opening, ...clampOpeningOnWall(host, opening) };
    })
    .filter((opening): opening is FloorPlanOpening => !!opening);
}

export function withWallEdit(
  data: BoardFloorPlanData,
  walls: FloorPlanWall[],
  constraints = data.constraints || [],
): BoardFloorPlanData {
  return {
    ...data,
    walls,
    constraints,
    openings: clampOpeningsToWalls(walls, data.openings),
  };
}

export function wallLabel(wall: FloorPlanWall): string {
  const len = wallLength(wall);
  const feet = Math.floor(len / 12);
  const inches = Math.round(len % 12);
  if (inches === 0) return `${feet}' wall`;
  return `${feet}'-${inches}" wall`;
}

export function otherConstraintWallId(constraint: FloorPlanConstraint, wallId: string): string | undefined {
  if (constraint.aWallId === wallId) return constraint.bWallId;
  if (constraint.bWallId === wallId) return constraint.aWallId;
  return undefined;
}

export function wallEndOnConstraint(
  constraint: FloorPlanConstraint,
  wallId: string,
): FloorPlanWallEnd | undefined {
  if (constraint.aWallId === wallId) return constraint.aEnd;
  if (constraint.bWallId === wallId) return constraint.bEnd;
  return undefined;
}

export function missingPerpPartners(
  walls: FloorPlanWall[],
  constraints: FloorPlanConstraint[] | undefined,
  wallId: string,
): FloorPlanWall[] {
  const wall = wallById(walls, wallId);
  if (!wall) return [];
  const seen = new Set<string>();
  const out: FloorPlanWall[] = [];
  for (const constraint of constraints || []) {
    if (constraint.kind !== 'coincident') continue;
    const otherId = otherConstraintWallId(constraint, wallId);
    if (!otherId || otherId === wallId || seen.has(otherId)) continue;
    const other = wallById(walls, otherId);
    if (!other || wallAxis(wall) === wallAxis(other)) continue;
    if (hasPerpendicular(constraints || [], wallId, otherId)) continue;
    seen.add(otherId);
    out.push(other);
  }
  return out;
}

export function addWallsToPlan(data: BoardFloorPlanData, added: FloorPlanWall[]): BoardFloorPlanData {
  const walls = [...data.walls, ...added];
  return normalizePlanExtents(withWallEdit(data, walls, inferConstraints(walls, data.constraints)));
}

export function hitTestWallEnd(
  walls: FloorPlanWall[],
  x: number,
  y: number,
  threshold: number,
  preferWallId?: string,
): { wall: FloorPlanWall; end: FloorPlanWallEnd } | null {
  const ordered = preferWallId
    ? [...walls.filter((wall) => wall.id === preferWallId), ...walls.filter((wall) => wall.id !== preferWallId)]
    : walls;
  let best: { wall: FloorPlanWall; end: FloorPlanWallEnd; dist: number } | null = null;
  for (const wall of ordered) {
    for (const end of ['start', 'end'] as FloorPlanWallEnd[]) {
      const point = wallEndPoint(wall, end);
      const dist = Math.hypot(x - point.x, y - point.y);
      if (dist <= threshold && (!best || dist < best.dist)) {
        best = { wall, end, dist };
      }
    }
  }
  return best ? { wall: best.wall, end: best.end } : null;
}
